import React, { useMemo, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { getUserData } from "./const";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import "../assets/css/theme-table-header.css";
import {
  listProducts,
  showProducts,
  showJangad,
  updateJangad,
  isErpApiSuccess,
  getErpAuthState,
} from "../erpClient";
import {
  normalizeProductStockListResponse,
  normalizeProductStockShowResponse,
} from "../utils/productStockErpAdapter";
import {
  createJangadIdempotencyKey,
  createVisitorsPayload,
  isJangadsApiSuccess,
  mapJangadShowResponseToLegacyOrder,
} from "../utils/jangadsErpAdapter";

const EDIT_JANGAD_FETCH_DEDUPE_MS = 2000;

let jangadShowPromise = null;
let jangadShowCache = null;
let jangadShowCacheId = null;
let jangadShowCacheAt = 0;

async function requestJangadShowForEdit(jangadId, { force = false } = {}) {
  const id = String(jangadId);
  const now = Date.now();

  if (!force) {
    if (jangadShowPromise) return jangadShowPromise;
    if (
      jangadShowCache &&
      jangadShowCacheId === id &&
      now - jangadShowCacheAt < EDIT_JANGAD_FETCH_DEDUPE_MS
    ) {
      return jangadShowCache;
    }
  } else {
    jangadShowPromise = null;
    jangadShowCache = null;
    jangadShowCacheId = null;
    jangadShowCacheAt = 0;
  }

  jangadShowPromise = showJangad({ id: jangadId })
    .then((res) => {
      if (isJangadsApiSuccess(res)) {
        jangadShowCache = res;
        jangadShowCacheId = id;
        jangadShowCacheAt = Date.now();
      }
      return res;
    })
    .finally(() => {
      jangadShowPromise = null;
    });

  return jangadShowPromise;
}

function EditJangad() {
  const { buttonClass, tableClass, cardClass, containerClass } =
    useButtonTableStyle();
  const navigate = useNavigate();
  const { state } = useLocation();
  const order = state?.JangadOrderData || null;
  const jangadId = state?.JangadOrderId || state?.id || null;

  const [clientName, setClientName] = useState("");
  const [clientMobile, setClientMobile] = useState("");
  const [orderType, setOrderType] = useState("Customer");
  const [expDelivery, setExpDelivery] = useState("");
  const [noOfVisitors, setNoOfVisitors] = useState(1);
  const [visitorNames, setVisitorNames] = useState("");
  const [visitorMobiles, setVisitorMobiles] = useState("");
  const [reference, setReference] = useState("");
  const [remarks, setRemarks] = useState("");
  const [productGroupNames, setProductGroupNames] = useState({});

  // Dropdown state
  const [dropdownData, setDropdownData] = useState({
    tagNo: [],
  });
  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownSearchText, setDropdownSearchText] = useState("");
  const [lastSearchedTagPrefix, setLastSearchedTagPrefix] = useState("");
  const [tagNoApiCalled, setTagNoApiCalled] = useState(false);
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const dropdownItemsRef = useRef([]);

  // Listen for API URL changes - navigate back to table
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in EditJangad, navigating back...', event.detail);
      toast('Database switched. Returning to jangad table.');
      navigate('/jangadtable');
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [navigate]);

  const seedProductGroupNamesFromEntries = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) return;
    const names = {};
    entries.forEach((entry) => {
      const pgId = entry.product_group ?? entry.product_type;
      const pgName = String(entry.product_group_name || "").trim();
      if (pgId != null && pgId !== "" && pgName) {
        names[pgId] = pgName;
        names[String(pgId)] = pgName;
      }
    });
    if (Object.keys(names).length === 0) return;
    setProductGroupNames((prev) => ({ ...prev, ...names }));
  };

  const applyMappedOrderHeader = (mapped) => {
    if (!mapped) return;

    if (mapped.name) setClientName(mapped.name);
    if (mapped.mobile) setClientMobile(mapped.mobile);
    if (!mapped.mobile && mapped.contact_number) {
      setClientMobile(mapped.contact_number);
    }

    if (mapped.exp_delivery) {
      const raw = String(mapped.exp_delivery || "").split(/[ T]/)[0];
      setExpDelivery(raw);
    }

    if (mapped.group_type) {
      setOrderType(
        String(mapped.group_type).charAt(0).toUpperCase() +
          String(mapped.group_type).slice(1).toLowerCase(),
      );
    }

    if (mapped.no_of_visitors) setNoOfVisitors(mapped.no_of_visitors);

    const vn = String(mapped.visitor_names ?? "").trim();
    const vm = String(mapped.visitor_mobiles ?? "").trim();
    const partyName = String(
      mapped.name || mapped.company_name || mapped.karigar_name || "",
    ).trim();
    const partyMobile = String(
      mapped.mobile || mapped.contact_number || "",
    ).trim();
    setVisitorNames(vn || partyName);
    setVisitorMobiles(vm || partyMobile);
    if (mapped.ref_name) setReference(mapped.ref_name);
    if (mapped.remarks) setRemarks(mapped.remarks);
  };

  const fetchTagNoByPrefix = async (prefix) => {
    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setDropdownData((prev) => ({ ...prev, tagNo: [] }));
      setTagNoApiCalled(false);
      setLastSearchedTagPrefix("");
      return;
    }

    if (lastSearchedTagPrefix === trimmed) {
      return;
    }

    try {
      const response = await listProducts({ search: trimmed });
      let products = normalizeProductStockListResponse(response);
      if (!products.length) {
        const showRes = await showProducts({ tag_no: trimmed });
        products = normalizeProductStockShowResponse(showRes);
      }

      if (products.length > 0) {
        const fetchedTags = products.map((item) => {
          if (typeof item === "string") {
            return { value: item, label: item, tag_no: item };
          }
          return {
            value: item.id,
            label: item.tag_no,
            ...item,
          };
        });
        setDropdownData((prev) => ({ ...prev, tagNo: fetchedTags }));
        setLastSearchedTagPrefix(trimmed);
        setTagNoApiCalled(true);
      } else {
        setDropdownData((prev) => ({ ...prev, tagNo: [] }));
        setTagNoApiCalled(true);
      }
    } catch (error) {
      console.error("Error fetching tag no options:", error);
      setDropdownData((prev) => ({ ...prev, tagNo: [] }));
      setTagNoApiCalled(true);
    }
  };

  const [localOrder, setLocalOrder] = useState(null);
  const [orderLoading, setOrderLoading] = useState(Boolean(jangadId));
  const jangadUpdateIdempotencyKeyRef = useRef(null);
  const jangadUpdatePayloadFingerprintRef = useRef(null);

  // Helper to map API order to local shape
  const mapOrderToLocal = (ord) => {
    const entries = Array.isArray(ord?.jangad_entries)
      ? ord.jangad_entries
      : [];
    return {
      ...ord,
      jangad_entries: entries.map((e, idx) => ({
        id: e.id || idx,
        tag_no: e.tag_no || e.tagNo || "",
        product_tag_no: e.product_tag_no || e.tag_no || "",
        product_id: e.product_id ?? e.productId ?? null,
        product_group: e.product_group || e.product_type || "",
        product_group_name: e.product_group_name || "",
        pcs: e.pcs || "",
        gwt: e.gwt || e.current_weight || "",
        lwt: e.lwt || "",
        nwt: e.nwt || e.exp_weight || "",
        remarks: e.item_remarks || "",
      })),
    };
  };

  // Fallback when opened without id (navigation payload only).
  useEffect(() => {
    if (jangadId || !order) return;
    const mapped = mapOrderToLocal(order);
    setLocalOrder(mapped);
    seedProductGroupNamesFromEntries(mapped.jangad_entries);
    applyMappedOrderHeader(order);
  }, [order, jangadId]);

  // Single show fetch when id is available (skip duplicate load from table state).
  useEffect(() => {
    if (!jangadId) return;

    let cancelled = false;

    const fetchById = async () => {
      setOrderLoading(true);
      try {
        const resp = await requestJangadShowForEdit(jangadId);
        if (cancelled || !isJangadsApiSuccess(resp)) return;

        const mapped = mapJangadShowResponseToLegacyOrder(resp);
        if (!mapped) return;

        const local = mapOrderToLocal(mapped);
        setLocalOrder(local);
        seedProductGroupNamesFromEntries(local.jangad_entries);
        applyMappedOrderHeader(mapped);
      } catch {
        // silent fail; UI will show no data
      } finally {
        if (!cancelled) setOrderLoading(false);
      }
    };

    fetchById();

    return () => {
      cancelled = true;
    };
  }, [jangadId]);

  const columns = useMemo(
    () => [
      { id: "no", label: "No.", width: "20px" },
      { id: "tag_no", label: "Tag No", width: "90px" },
      { id: "product_group", label: "Product Group", width: "250px" },
      { id: "pcs", label: "Pcs", width: "70px" },
      { id: "gwt", label: "GWt", width: "90px" },
      { id: "lwt", label: "LWt", width: "90px" },
      { id: "nwt", label: "NWt", width: "90px" },
      { id: "remarks", label: "Remarks", width: "240px" },
    ],
    []
  );

  // Function to send tag number to API and auto-fill product group
  const sendTagNoToApi = async (tagNo, rowIdx) => {
    try {
      const response = await showProducts({ tag_no: tagNo });
      const prods = normalizeProductStockShowResponse(response);
      const product = prods[0] || null;

      if (product) {
        setLocalOrder((prev) => {
          const copy = { ...prev };
          if (copy.jangad_entries[rowIdx]) {
            // Update product group
            if (product.product_group) {
              const pgId = product.product_group;
              const pgName =
                product.product_group_name ||
                product.product_group_label ||
                productGroupNames[pgId] ||
                String(pgId);
              copy.jangad_entries[rowIdx].product_group = pgId;
              copy.jangad_entries[rowIdx].product_group_name = pgName;
            }
            if (product.tag_no) {
              copy.jangad_entries[rowIdx].product_tag_no = product.tag_no;
            }
            // Update other fields if needed
            if (product.current_pcs) {
              copy.jangad_entries[rowIdx].pcs = product.current_pcs;
            }
            if (product.current_gwt) {
              copy.jangad_entries[rowIdx].gwt = product.current_gwt;
            }
            if (product.LWT) {
              copy.jangad_entries[rowIdx].lwt = product.LWT;
            }
            if (product.NWT) {
              copy.jangad_entries[rowIdx].nwt = product.NWT;
            }
            // Recalculate NWt if GWt or LWt changed
            const g = parseFloat(copy.jangad_entries[rowIdx].gwt || 0) || 0;
            const l = parseFloat(copy.jangad_entries[rowIdx].lwt || 0) || 0;
            copy.jangad_entries[rowIdx].nwt = (g - l).toFixed(2);
          }
          return copy;
        });
      }
    } catch (error) {
      console.error("Error in API call:", error);
    }
  };

  const handleCellChange = (rowIdx, field, value) => {
    setLocalOrder((prev) => {
      const copy = { ...prev };
      copy.jangad_entries = copy.jangad_entries.map((e, i) =>
        i === rowIdx ? { ...e, [field]: value } : e
      );
      // Keep NWt in sync if GWt or LWt edited
      if (field === "gwt" || field === "lwt") {
        const g = parseFloat(copy.jangad_entries[rowIdx].gwt || 0) || 0;
        const l = parseFloat(copy.jangad_entries[rowIdx].lwt || 0) || 0;
        copy.jangad_entries[rowIdx].nwt = (g - l).toFixed(2);
      }
      return copy;
    });
  };

  // Dropdown functions
  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    const row = localOrder?.jangad_entries?.[rowIndex];
    const initialSearch =
      columnType === "tagNo" ? String(row?.tag_no || "") : "";
    setDropdownSearchText(initialSearch);
    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setHighlightedIndex(0);
    if (columnType === "tagNo" && initialSearch.length >= 3) {
      setTagNoApiCalled(false);
      void fetchTagNoByPrefix(initialSearch);
    }
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    const value =
      typeof selectedItem === "object" ? selectedItem.label : selectedItem;
    const columnId = columns[colIndex]?.id;

    // Clear dropdown search text when selecting
    setDropdownSearchText("");
    handleCellChange(
      rowIndex,
      columnId === "tag_no" ? "tag_no" : columnId,
      value
    );
    setActiveDropdown({ row: null, col: null, type: null });

    // If tag_no is selected, call API to auto-fill product group
    if (columnId === "tag_no" && value && selectedItem) {
      sendTagNoToApi(value, rowIndex);
    }
  };

  const getFilteredItems = (searchText, columnType) => {
    const dataKey = columnType === "tagNo" ? "tagNo" : columnType;
    if (!dropdownData[dataKey]) return [];

    const textToFilter =
      activeDropdown.row !== null ? dropdownSearchText : searchText || "";
    const normalizedSearchText = textToFilter
      ? String(textToFilter).toLowerCase()
      : "";

    if (dataKey === "tagNo") {
      if (normalizedSearchText.length < 3) return [];
      if (!tagNoApiCalled) return [];
    }

    if (!normalizedSearchText) return dropdownData[dataKey];

    return dropdownData[dataKey].filter((item) => {
      if (typeof item === "object") {
        return item.label
          ? String(item.label).toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return String(item).toLowerCase().includes(normalizedSearchText);
    });
  };

  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    if (columnType === "tag_no") {
      const cell = localOrder?.jangad_entries[rowIndex];
      if (!cell) return;

      // Use dropdownSearchText for filtering
      const searchText = dropdownSearchText || "";
      const filtered = getFilteredItems(searchText, "tagNo");

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex]
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
      }
    }
  };

  // Render dropdown
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const row = localOrder?.jangad_entries[activeDropdown.row];
    if (!row) return null;

    // Use dropdownSearchText for filtering, not the actual cell value
    const searchText = dropdownSearchText || "";
    const filtered = getFilteredItems(searchText, activeDropdown.type);

    dropdownItemsRef.current = [];

    if (!document.getElementById("dropdown-portal")) {
      const portalDiv = document.createElement("div");
      portalDiv.id = "dropdown-portal";
      document.body.appendChild(portalDiv);
    }

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          minWidth: `${dropdownPosition.width}px`,
          maxWidth: `${dropdownPosition.width}px`,
          maxHeight: "200px",
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const itemLabel =
              typeof item === "object" ? item.label || "" : item;
            const row = localOrder?.jangad_entries[activeDropdown.row];
            const cellValue =
              activeDropdown.type === "tagNo" ? row?.tag_no || "" : "";
            const isSelected =
              itemLabel.toLowerCase() === (cellValue || "").toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (dropdownItemsRef.current[idx] = el)}
                className={`dropdown-item ${
                  idx === highlightedIndex ? "active" : ""
                }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === highlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                      ? "#f0f0f0"
                      : "transparent",
                  color: idx === highlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onMouseDown={() =>
                  handleDropdownSelect(
                    activeDropdown.row,
                    activeDropdown.col,
                    item
                  )
                }
              >
                {itemLabel}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">
            {String(dropdownSearchText || "").trim().length < 3
              ? ""
              : ""}
          </div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
        setDropdownSearchText(""); // Clear search text when closing
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  if (!localOrder) {
    if (orderLoading) {
      return (
        <div className="body-wrapper">
          <div className="container-fluid">
            <div className="alert alert-info mt-3">Loading jangad order...</div>
          </div>
        </div>
      );
    }
    return (
      <div className="body-wrapper">
        <div className="container-fluid">
          <div className="alert alert-warning mt-3">
            No order data to edit. Please open this page from Jangad Table.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className={`container-fluid ${cardClass}`}>
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Edit Jangad</h4>
          </div>

          <div className="card-body">
            {/* Header like Add Jangad: Exp Delivery, Type, Select (Mobile), Name */}
            <div className="row mb-1">
              {/* Exp Delivery */}
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "90px" }}
                  >
                    Exp Delivery:
                  </label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={expDelivery}
                    onChange={(e) => setExpDelivery(e.target.value)}
                  />
                </div>
              </div>

              {/* Order Type Dropdown */}
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0 fw-semibold"
                    style={{ width: "80px" }}
                  >
                    Type:
                  </label>
                  <select
                    className={`form-select form-select-sm ${buttonClass}`}
                    value={orderType}
                    onChange={(e) => setOrderType(e.target.value)}
                  >
                    <option value="Customer">Customer</option>
                    <option value="Supplier/Karigar">Supplier/Karigar</option>
                  </select>
                </div>
              </div>

              {/* Customer Name */}
              <div className="col-lg-3 col-md-5 col-sm-6 mb-2">
                <div className="d-flex align-items-center position-relative">
                  <label
                    className="form-label me-1 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "55px" }}
                  >
                    Name:
                  </label>
                  <div className="position-relative flex-grow-1">
                    <input
                      type="text"
                      className={`form-control form-control-sm ${buttonClass}`}
                      placeholder="Name"
                      value={clientName}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Customer Mobile */}
              <div className="col-lg-2 col-md-7 col-sm-12 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    placeholder="Mobile No"
                    value={clientMobile}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div
              className={`table-responsive border mt-3 ${cardClass}`}
              style={{
                maxHeight: "calc(38px * 7)",
                overflowY: "auto",
                position: "relative",
              }}
            >
              <table
                className={`table table-bordered table-hover table-sm mb-0 theme-header-table ${tableClass}`}
              >
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: c.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {localOrder.jangad_entries.map((row, idx) => (
                    <tr
                      key={row.id || idx}
                      style={{ backgroundColor: "#ffffff" }}
                    >
                      <td className="p-0" style={{ width: columns[0]?.width }}>
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{ height: "38px" }}
                        >
                          <span style={{ fontSize: "0.85rem" }}>{idx + 1}</span>
                        </div>
                      </td>
                      <td className="p-0">
                        <div className="position-relative">
                          <input
                            ref={(el) => (inputRefs.current[`${idx}-1`] = el)}
                            className={`form-control border-0 ${buttonClass}`}
                            style={{
                              fontSize: "0.85rem",
                              padding: "0.4rem 0.5rem",
                              backgroundColor: "transparent",
                              width: "100%",
                              transition: "background-color 0.2s",
                            }}
                            value={row.tag_no || ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              handleCellChange(idx, "tag_no", newValue);
                              setDropdownSearchText(newValue);
                              if (newValue.length >= 3) {
                                setTagNoApiCalled(false);
                                void fetchTagNoByPrefix(newValue);
                              } else {
                                setLastSearchedTagPrefix("");
                                setTagNoApiCalled(false);
                                setDropdownData((prev) => ({
                                  ...prev,
                                  tagNo: [],
                                }));
                              }
                              const columnType = "tagNo";
                              if (
                                activeDropdown.row === idx &&
                                activeDropdown.col === 1 &&
                                activeDropdown.type === columnType
                              ) {
                                // Update position in case of scroll
                                const inputElement =
                                  inputRefs.current[`${idx}-1`];
                                if (inputElement) {
                                  const rect =
                                    inputElement.getBoundingClientRect();
                                  setDropdownPosition({
                                    top: rect.bottom + window.scrollY,
                                    left: rect.left + window.scrollX,
                                    width: rect.width,
                                  });
                                }
                              } else {
                                setActiveDropdown({
                                  row: idx,
                                  col: 1,
                                  type: columnType,
                                });
                                const inputElement =
                                  inputRefs.current[`${idx}-1`];
                                if (inputElement) {
                                  const rect =
                                    inputElement.getBoundingClientRect();
                                  setDropdownPosition({
                                    top: rect.bottom + window.scrollY,
                                    left: rect.left + window.scrollX,
                                    width: rect.width,
                                  });
                                }
                              }
                            }}
                            onKeyDown={(e) =>
                              handleKeyDown(e, idx, 1, "tag_no")
                            }
                            onClick={() => handleDropdownClick(idx, 1, "tagNo")}
                            onFocus={() => {
                              const inputElement =
                                inputRefs.current[`${idx}-1`];
                              if (inputElement) {
                                inputElement.style.backgroundColor = "#fff";
                              }
                              handleDropdownClick(idx, 1, "tagNo");
                            }}
                            onBlur={(e) => {
                              e.target.style.backgroundColor = "transparent";
                            }}
                            autoComplete="off"
                          />
                        </div>
                      </td>
                      <td className="p-0">
                        <input
                          className={`form-control border-0 ${buttonClass}`}
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          value={
                            row.product_group_name ||
                            productGroupNames[row.product_group] ||
                            row.product_group ||
                            ""
                          }
                          onChange={(e) =>
                            handleCellChange(
                              idx,
                              "product_group",
                              e.target.value
                            )
                          }
                        />
                      </td>
                      <td className="p-0">
                        <input
                          type="number"
                          className={`form-control border-0 ${buttonClass}`}
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          value={row.pcs}
                          onChange={(e) =>
                            handleCellChange(idx, "pcs", e.target.value)
                          }
                        />
                      </td>
                      <td className="p-0">
                        <input
                          type="number"
                          className={`form-control border-0 ${buttonClass}`}
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          value={row.gwt}
                          onChange={(e) =>
                            handleCellChange(idx, "gwt", e.target.value)
                          }
                        />
                      </td>
                      <td className="p-0">
                        <input
                          type="number"
                          className={`form-control border-0 ${buttonClass}`}
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          value={row.lwt}
                          onChange={(e) =>
                            handleCellChange(idx, "lwt", e.target.value)
                          }
                        />
                      </td>
                      <td className="p-0">
                        <input
                          type="number"
                          className={`form-control border-0 ${buttonClass}`}
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          value={row.nwt}
                          onChange={(e) =>
                            handleCellChange(idx, "nwt", e.target.value)
                          }
                        />
                      </td>
                      <td className="p-0">
                        <input
                          className={`form-control border-0 ${buttonClass}`}
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.5rem",
                            backgroundColor: "transparent",
                            width: "100%",
                            transition: "background-color 0.2s",
                          }}
                          value={row.remarks}
                          onChange={(e) =>
                            handleCellChange(idx, "remarks", e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                  {/* Add Totals Row (only GWt, LWt, NWt) */}
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    {columns.map((col, colIdx) => {
                      if (col.id === "no") {
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ width: col.width }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                Total
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "gwt") {
                        const totalGWt = localOrder.jangad_entries
                          .reduce((sum, row) => {
                            const gwt = parseFloat(row.gwt || 0);
                            return sum + (isNaN(gwt) ? 0 : gwt);
                          }, 0)
                          .toFixed(2);
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ backgroundColor: "#fff" }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-end px-2"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                {totalGWt}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "lwt") {
                        const totalLWt = localOrder.jangad_entries
                          .reduce((sum, row) => {
                            const lwt = parseFloat(row.lwt || 0);
                            return sum + (isNaN(lwt) ? 0 : lwt);
                          }, 0)
                          .toFixed(2);
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ backgroundColor: "#fff" }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-end px-2"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                {totalLWt}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "nwt") {
                        const totalNWt = localOrder.jangad_entries
                          .reduce((sum, row) => {
                            const nwt = parseFloat(row.nwt || 0);
                            return sum + (isNaN(nwt) ? 0 : nwt);
                          }, 0)
                          .toFixed(2);
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ backgroundColor: "#fff" }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-end px-2"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                {totalNWt}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.id === "pcs") {
                        const totalPcs = localOrder.jangad_entries.reduce(
                          (sum, row) => {
                            const pcs = parseFloat(row.pcs || 0);
                            return sum + (isNaN(pcs) ? 0 : pcs);
                          },
                          0
                        );
                        return (
                          <td
                            key={col.id}
                            className="p-0"
                            style={{ backgroundColor: "#fff" }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-end px-2"
                              style={{ height: "38px" }}
                            >
                              <span
                                style={{
                                  fontSize: "0.85rem",
                                  fontWeight: "600",
                                }}
                              >
                                {totalPcs}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      // All other columns in totals row stay blank
                      return <td key={col.id} className="p-0"></td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            {/* Visitor Fields Section - Below Table (same as Add) */}
            <div className="row mb-3 mt-3">
              <div className="col-lg-6 col-md-6 col-sm-6">
                <div className="d-flex align-items-center mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "100px" }}
                  >
                    No of Visitors:
                  </label>
                  <input
                    type="number"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={noOfVisitors}
                    onChange={(e) => setNoOfVisitors(e.target.value)}
                    min="1"
                    placeholder="Number"
                  />
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "100px" }}
                  >
                    Visitor Names:
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={visitorNames}
                    onChange={(e) => setVisitorNames(e.target.value)}
                    placeholder="Enter visitor names"
                  />
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "100px" }}
                  >
                    Visitor Mobiles:
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={visitorMobiles}
                    onChange={(e) => setVisitorMobiles(e.target.value)}
                    placeholder="Mobile No(s), comma separated"
                  />
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "100px" }}
                  >
                    Reference Person:
                  </label>
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    placeholder="Enter reference person"
                  />
                </div>

                <div className="d-flex align-items-start mb-3">
                  <label
                    className="form-label me-2 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "100px", paddingTop: "6px" }}
                  >
                    Remarks:
                  </label>
                  <textarea
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter remarks"
                    maxLength="200"
                    rows="3"
                    style={{ resize: "vertical" }}
                  />
                </div>
              </div>
            </div>

            {/* Bottom actions */}
            <div className="text-end mt-3">
              <button
                className={`btn btn-primary ${buttonClass}`}
                onClick={async () => {
                  if (
                    !localOrder ||
                    !localOrder.jangad_entries ||
                    localOrder.jangad_entries.length === 0
                  ) {
                    toast.error(
                      "Please add at least one item to the jangad order"
                    );
                    return;
                  }

                  // Legacy login uses encrypted `userData`; ERP OTP uses plain `user-details` (getErpAuthState).
                  const legacyUser =
                    (typeof getUserData === "function" ? getUserData() : null) ||
                    {};
                  const erpUser = getErpAuthState?.()?.user || {};
                  const resolvedUserId =
                    legacyUser?.id ??
                    legacyUser?.ua_id ??
                    legacyUser?.user_id ??
                    erpUser?.id ??
                    erpUser?.ua_id ??
                    erpUser?.user_id;
                  if (
                    resolvedUserId == null ||
                    resolvedUserId === ""
                  ) {
                    toast.error("User data not found. Please login again.");
                    return;
                  }

                  // Get group_id from localOrder
                  const group_id = localOrder.group_id || order?.group_id;
                  if (!group_id) {
                    toast.error("Group ID not found");
                    return;
                  }

                  const hasAnyReturnedItem = Array.isArray(
                    localOrder.jangad_entries,
                  )
                    ? localOrder.jangad_entries.some(
                        (e) => e?.status === "2" || e?.status === 2,
                      )
                    : false;

                  if (hasAnyReturnedItem) {
                    toast.error(
                      "Some jangad items are already received. Use settlement instead."
                    );
                    return;
                  }

                  if (!expDelivery) {
                    toast.error("Expected return date is required");
                    return;
                  }

                  const party_type = String(orderType || "")
                    .toLowerCase()
                    .includes("customer")
                    ? "CUSTOMER"
                    : "SUPPLIER";

                  try {
                    // API requires product_id — prefer id from showJangad line, else resolve by tag_no.
                    const apiItems = await Promise.all(
                      localOrder.jangad_entries.map(async (entry) => {
                        const rawPid =
                          entry.product_id ?? entry.productId ?? null;
                        const fromShow =
                          rawPid != null &&
                          rawPid !== "" &&
                          Number.isFinite(Number(rawPid)) &&
                          Number(rawPid) > 0
                            ? Number(rawPid)
                            : null;

                        let productId = fromShow;
                        const tagNo = String(
                          entry.product_tag_no || entry.tag_no || "",
                        ).trim();

                        if (productId == null && tagNo) {
                          const productRes = await showProducts({
                            tag_no: tagNo,
                          });
                          const product =
                            productRes?.data?.product ??
                            productRes?.data?.data?.product ??
                            null;
                          productId =
                            product?.id != null ? Number(product.id) : null;
                        }

                        if (productId == null || !Number.isFinite(productId)) {
                          toast.error(
                            `Unable to resolve product for tag${tagNo ? `: ${tagNo}` : ""}. Check tag or stock.`,
                          );
                          throw new Error(
                            `Unable to resolve product for tag_no: ${tagNo || "(empty)"}`,
                          );
                        }

                        const gwt = Number(entry.gwt ?? 0) || 0;
                        const nwt = Number(entry.nwt ?? 0) || 0;
                        const lwt = Number(entry.lwt ?? 0) || 0;
                        const weight =
                          Number(entry.nwt ?? entry.gwt ?? 0) || 0;

                        return {
                          product_id: productId,
                          pcs: Number(entry.pcs ?? 0) || 0,
                          item_remarks: entry.remarks || "",
                          weight,
                          gwt,
                          nwt,
                          lwt,
                        };
                      }),
                    );

                    const visitors = createVisitorsPayload({
                      visitorNames,
                      visitorMobiles,
                      noOfVisitors,
                    });

                    const basePayload = {
                      party_type,
                      party_id: Number(group_id),
                      date: expDelivery,
                      expected_return: expDelivery,
                      remarks,
                      reference: reference ?? "",
                      visitors,
                      items: apiItems,
                    };

                    const fingerprint = JSON.stringify(basePayload);

                    if (
                      !jangadUpdateIdempotencyKeyRef.current ||
                      jangadUpdatePayloadFingerprintRef.current !== fingerprint
                    ) {
                      jangadUpdateIdempotencyKeyRef.current =
                        createJangadIdempotencyKey();
                      jangadUpdatePayloadFingerprintRef.current = fingerprint;
                    }

                    const response = await updateJangad({
                      id: jangadId,
                      idempotency_key:
                        jangadUpdateIdempotencyKeyRef.current,
                      ...basePayload,
                    });

                    if (isJangadsApiSuccess(response)) {
                      toast.success("Jangad order updated successfully!");

                      jangadUpdateIdempotencyKeyRef.current = null;
                      jangadUpdatePayloadFingerprintRef.current = null;

                      const jangad_document_id =
                        response?.jangad_document_id ?? jangadId;
                      try {
                        await showJangad({ id: jangad_document_id });
                      } catch {
                        // ignore refresh failure
                      }

                      navigate("/Jangad");
                    } else {
                      toast.error(
                        response?.message || "Failed to update jangad order",
                      );
                    }
                  } catch (error) {
                    console.error("Error updating jangad order:", error);
                    toast.error(
                      error?.response?.data?.message ||
                        error?.message ||
                        "Failed to update jangad order",
                    );
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      {renderDropdown()}
    </div>
  );
}

export default EditJangad;
